import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS } from '../../constants/imageConstants';
import ListSkeleton from '../../components/skeletons/ListSkeleton';
import { timeAgo } from '../../utils/dateUtils';
import DataListContainer from '../../components/layout/DataListContainer';
import { toastService } from '../../services/toastService';
import { CalendarIcon } from '../../components/Icons/svgIcons';

const RecentSubmissions = ({ submissions, status }) => {
  const navigate = useNavigate();

  const recentSubmissions = useMemo(() => {
    if (!Array.isArray(submissions)) return [];
    return [...submissions]
      .filter(submission => submission.formStatus === 'published' || !submission.formStatus)
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
      .slice(0, 5);
  }, [submissions]);

  const handleViewSubmission = (id) => {
    if (!navigator.onLine) {
      toastService.warning('Synced Submissions cannot be viewed offline.');
      return;
    }
    navigate(`/forms/data/${id}`);
  };

  const getStatusBadge = (syncStatus) => {
    const isPending = syncStatus === 'pending';
    return (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
        isPending 
          ? 'bg-yellow-50 text-yellow-700 border-yellow-200' 
          : 'bg-green-50 text-green-700 border-green-200'
      }`}>
        {isPending ? 'Pending' : 'Synced'}
      </span>
    );
  };

  return (
    <div className="rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b-2 border-gray-200">
        <h3 className="text-gray-900 text-sm font-bold uppercase tracking-wide">Recent Submissions</h3>
      </div>
      
      <DataListContainer
        isLoading={status === 'loading' && recentSubmissions.length === 0}
        data={recentSubmissions}
        skeletonComponent={<div className="p-4"><ListSkeleton items={3} /></div>}
        emptyStateContainerClassName="p-6"
        emptyStateProps={{
          title: "No Recent Submissions",
          message: "Your latest submissions will appear here."
        }}
      >
        <div className="divide-y divide-gray-200">
          {recentSubmissions.map((submission) => (
            <div 
              key={submission.id}
              className="p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors group"
              onClick={() => handleViewSubmission(submission.id, submission.syncStatus)}
            >
              <div className="flex justify-between items-start mb-1.5">
                <p className="text-sm font-semibold text-gray-800 line-clamp-1 pr-2 group-hover:text-[rgb(var(--primary))] transition-colors">
                  {submission.formName || 'Untitled Form'}
                </p>
                {getStatusBadge(submission.syncStatus)}
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span>{timeAgo(submission.created_at || submission.createdAt)}</span>
                </div>
                  <img 
                    src={ICONS.ARROW_RIGHT} 
                    alt="" 
                    className="w-4 h-4 opacity-40"
                  />
              </div>
            </div>
          ))}
        </div>
      </DataListContainer>
    </div>
  );
};

export default RecentSubmissions;